/**
 * @author JuhaK
 */
import { ILayerWrapperOptions, LayerWrapper } from '../LayerWrapper';
import { GtPointFeatureFactory } from './GtPointFeatureFactory';
import { makeObservable, observable } from 'mobx';
import { Notify } from '@shared/ui-native/Notify';
import WebGLPointsLayer from 'ol/layer/WebGLPoints';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import WebGLVectorLayerRenderer from 'ol/renderer/webgl/VectorLayer';
import Layer from 'ol/layer/Layer';
import Feature from 'ol/Feature';
import { MultiLineString, Point as OlPoint } from 'ol/geom';
import { latLonToEN, removeFeaturesBySource, WEBGL_NOT_HIDDEN_FILTER } from '@openlayers/ol-utils';
import * as olStyle from 'ol/style';
import { getGtPointWebGlStyle } from '@shared/codelist/point-service';

class DeviationsWebGLLayer extends Layer {
    createRenderer() {
        return new WebGLVectorLayerRenderer(this, {
            style: {
                'stroke-color': [0, 0, 0],
                'stroke-width': 2,
            },
            variables: {},
        });
    }
}

export class GtPointsLayer extends LayerWrapper<IOutlinePoint> {
    featureType: FeatureType = 'model_point';

    @observable public labelProps: string[] = [];
    @observable showDeviations = true;

    // we draw all point on webgl layer, and since it doesn't handle text, we draw labels on separate layer when zoomed in
    labelsLayer: VectorLayer<any> = null;

    deviationsLayer: DeviationsWebGLLayer = new DeviationsWebGLLayer({
        source: new VectorSource(),
    });
    deviationsLabelsLayer: VectorLayer<any> = new VectorLayer({
        source: new VectorSource(),
        minZoom: 19,
    });
    hitTargetLayer: WebGLPointsLayer<any> = null;
    deviationsLayerAdded = false;

    constructor(opts: ILayerWrapperOptions<IOutlinePoint>) {
        opts.layerBuilderFn = () => new WebGLPointsLayer({ style: getGtPointWebGlStyle(), filter: WEBGL_NOT_HIDDEN_FILTER });

        super(opts);
        makeObservable(this);

        if (window.innerWidth < 800) {
            this.hitTargetLayer = new WebGLPointsLayer({
                source: this.layerSource,
                filter: WEBGL_NOT_HIDDEN_FILTER,
                style: {
                    'circle-radius': 14,
                    'circle-fill-color': 'rgba(0,0,0,0.05)' /* 0.05 is hardcoded cutoff for events in openlayers, can't go lower */,
                    'circle-opacity': 1,
                    'circle-scale': ['interpolate', ['exponential', 2], ['zoom'], 8, 0.2, 15, 1],
                },
            });
        }

        const deviationsSource = this.deviationsLayer.getSource() as VectorSource;
        const addOrRemoveDeviationsLayer = _.debounce(() => {
            if (!this.showDeviations || deviationsSource.isEmpty()) {
                this.removeDeviationsLayer();
            } else {
                this.addDeviationsLayer();
            }
        }, 10);
        deviationsSource.on('addfeature', addOrRemoveDeviationsLayer);
        deviationsSource.on('removefeature', addOrRemoveDeviationsLayer);
    }

    drawAll(items: IOutlinePoint[], structures?: IStructures[]): { features: Feature[]; failedToCreate: IOutlinePoint[] } {
        const pointFeatures = super.drawAll(items, structures);
        this.drawDeviationVectors(pointFeatures?.features);
        return pointFeatures;
    }

    deviationForFeature: WeakMap<Feature, boolean> = new WeakMap();

    // careful to only draw these when showDeviations=true, do not pre-create along with gt points
    // every point feature requires +1 feature for the deviation label, and it takes a lot of memory
    // deviation lines are optimized by drawing single multilinestring with fake arrowheads, one per model-id
    // todo: only create label features when zoomed in and in view extent, would also help with memory spikes
    drawDeviationVectors = (features?: Feature[]) => {
        if (!this.showDeviations) return;

        const featuresWithoutDeviation = features ?? this.layerSource.getFeatures().filter(f => !this.deviationForFeature.has(f));
        const groupedById = _.groupBy(featuresWithoutDeviation, f => f.get('data').sourceModelId);

        Object.entries(groupedById).forEach(([id, features]) => {
            const source: FeatureSource = {
                modelId: parseInt(id) ?? -1,
            };
            const allLines = [];

            features.forEach(feature => {
                const i = feature.get('data');
                if (!i) return;

                const devA = i.props?.differenceA || i.props?.D_NORTH;
                const devB = i.props?.differenceB || i.props?.D_EAST;
                const devXY = i.props?.differenceXY || i.props?.DIFF_ERROR;

                if (devA && devB) {
                    const start = latLonToEN(i.lat, i.lon);
                    const displaced = [start[0] + parseFloat(devB), start[1] + parseFloat(devA)];
                    const angle = Math.atan2(displaced[1] - start[1], displaced[0] - start[0]);
                    const cos = Math.cos(angle);
                    const sin = Math.sin(angle);

                    const rotatePoint = (x: number, y: number) => {
                        const dx = x - start[0];
                        const dy = y - start[1];
                        return [start[0] + dx * cos - dy * sin, start[1] + dx * sin + dy * cos];
                    };

                    const end = rotatePoint(start[0] + 1, start[1]);
                    const arrowLeft = rotatePoint(start[0] + 0.7, start[1] + 0.2);
                    const arrowRight = rotatePoint(start[0] + 0.7, start[1] - 0.2);

                    allLines.push([
                        [start[0], start[1]],
                        [end[0], end[1]],
                    ]);
                    // temp disable arrowheads to fix "Vertex buffer is not big enough for the draw call"
                    // shouldnt be a problem when we update upenlayers soon, https://github.com/openlayers/openlayers/pull/16840
                    // allLines.push([[arrowLeft[0], arrowLeft[1]], [end[0], end[1]], [arrowRight[0], arrowRight[1]]]);

                    const labelPoint = new OlPoint([start[0] + 2, start[1]]);
                    labelPoint.rotate(angle, start);
                    const labelF = new Feature({
                        geometry: labelPoint,
                        source,
                    });
                    labelF.setStyle(
                        new olStyle.Style({
                            text: new olStyle.Text({
                                text: parseFloat(devXY).toFixed(3),
                                overflow: true,
                                font: '13px monospace',
                                offsetY: -2,
                                textAlign: 'center',
                                textBaseline: 'middle',
                                fill: new olStyle.Fill({
                                    color: 'black',
                                }),
                            }),
                        })
                    );
                    this.deviationForFeature.set(feature, true);
                    (this.deviationsLabelsLayer.getSource() as VectorSource).addFeature(labelF);
                }
            });

            if (allLines.length > 0) {
                (this.deviationsLayer.getSource() as VectorSource).addFeature(
                    new Feature({
                        geometry: new MultiLineString(allLines),
                        source,
                    })
                );
            }
        });
    };

    removeFeaturesBySource(source: FeatureSource, silent = true) {
        removeFeaturesBySource(this.deviationsLayer.getSource() as VectorSource, source, false);
        removeFeaturesBySource(this.deviationsLabelsLayer.getSource() as VectorSource, source, false);
        super.removeFeaturesBySource(source, silent);
    }

    onAddedToMap() {
        if (!this.labelsLayer) {
            this.labelsLayer = new VectorLayer({
                source: this.layerSource,
                updateWhileAnimating: false,
                updateWhileInteracting: false,
                minZoom: 17,
            });

            this.labelsLayer.setStyle(feature => {
                const isLinePoint = feature.get('isLinePoint');
                const showPoints = !isLinePoint || (isLinePoint && feature.get('showLabel'));
                return GtPointFeatureFactory.styleFunction(feature, showPoints ? this.labelProps : []);
            });
        }

        this.map.addLayer(this.labelsLayer);

        if (this.hitTargetLayer) {
            this.map.addLayer(this.hitTargetLayer);
            this.hitTargetLayer.setZIndex(this.layer.getZIndex() - 1);
        }

        if (this.showDeviations) {
            this.addDeviationsLayer();
        }
    }

    onRemovedFromMap() {
        this.removeDeviationsLayer();
        this.map.removeLayer(this.labelsLayer);
        if (this.hitTargetLayer) {
            this.map.removeLayer(this.hitTargetLayer);
        }
    }

    addDeviationsLayer = () => {
        this.drawDeviationVectors();
        if ((this.deviationsLayer.getSource() as VectorSource).isEmpty()) {
            return;
        }
        if (this.deviationsLayerAdded) return;
        this.deviationsLayerAdded = true;

        this.map.addLayer(this.deviationsLayer);
        this.deviationsLayer.setZIndex(this.layer.getZIndex() - 1);
        this.map.addLayer(this.deviationsLabelsLayer);
        this.deviationsLabelsLayer.setZIndex(this.layer.getZIndex() + 1);
    };

    removeDeviationsLayer = () => {
        if (!this.deviationsLayerAdded) return;
        this.deviationsLayerAdded = false;
        this.map.removeLayer(this.deviationsLayer);
        this.map.removeLayer(this.deviationsLabelsLayer);
        // just remove layers from map and dont clear features from source, because memory is not properly released if we recreate them
    };

    setLabelProps(props: string | string[]) {
        this.labelProps = Array.isArray(props) ? props : [props];
        if (this.labelProps.length === 0 && this.labelsLayer) {
            this.map.removeLayer(this.labelsLayer);
            this.labelsLayer.dispose();
            this.labelsLayer = null;
        } else if (!this.labelsLayer) {
            this.onAddedToMap();
        }
        this.layerSource.dispatchEvent('change');
    }

    toggleLabelProp(prop: string) {
        const props = new Set(this.labelProps);
        if (props.has(prop)) {
            props.delete(prop);
        } else {
            if (this.labelProps.length >= 3) {
                Notify.warn('Up to 3 labels are allowed');
                return;
            }
            props.add(prop);
        }
        this.labelProps = Array.from(props);
        this.layerSource.dispatchEvent('change');
    }

    toggleDeviations = () => {
        this.setShowDeviations(!this.showDeviations);
    };

    setShowDeviations = (show: boolean) => {
        this.showDeviations = show;

        if (this.showDeviations) {
            this.addDeviationsLayer();
        } else {
            this.removeDeviationsLayer();
        }

        setTimeout(() => {
            this.layerSource.dispatchEvent('change');
            this.map.getView().adjustZoom(0.0001);
        });
    };

    logStats = () => {
        console.log('gt points', this.layerSource.getFeatures().length);
        console.log('deviation lines', (this.deviationsLayer.getSource() as VectorSource).getFeatures().length);
        console.log('deviation labels', this.deviationsLabelsLayer.getSource().getFeatures().length);
    };
}
